// Hub de diffusion Realtime (spec 2026-10-10 §3) : un canal par sujet exact,
// partagé par compteur de références entre autant d'écouteurs qu'il faut.
// - Sujet privé : jamais rejoint sans session, ni pour un autre compte que
//   celui de l'écouteur ; setAuth(jeton) est attendu avant chaque subscribe().
// - Jeton rafraîchi : setAuth(nouveau jeton), poussé par realtime-js aux
//   canaux joints, sans les quitter.
// - Déconnexion / changement de compte : les canaux privés sont fermés et
//   removeChannel est attendu avant toute nouvelle jonction (realtime-js rend
//   le même canal pour un sujet tant qu'il n'est pas retiré).
// - CHANNEL_ERROR / TIMED_OUT / CLOSED : nouvelle jonction après 1 s, 2 s,
//   4 s… plafonnée à 30 s, sans jamais abandonner ; recheck() (onglet visible,
//   réseau revenu) relance tout de suite.
// - Après chaque jonction réussie, onResync de chaque écouteur (rattrapage).
// - Écouteur privé dont le compte n'est pas (encore) le compte visé : garé
//   (jamais de message) plutôt que raccroché à une entrée d'un autre compte ;
//   adopté dès que ce compte devient celui visé (voir `parked`/`adoptParkedFor`).
// Aucun import React ni supabase : testé avec un faux client.
import { realTimers, type Timers } from './coalesce';

export type SubscribeStatus = 'SUBSCRIBED' | 'CHANNEL_ERROR' | 'TIMED_OUT' | 'CLOSED';

export interface HubMessage {
    event?: string;
    payload?: unknown;
}

export interface HubChannel {
    on(type: 'broadcast', filter: { event: string }, callback: (message: HubMessage) => void): HubChannel;
    subscribe(callback: (status: string, err?: Error) => void): HubChannel;
}

export interface HubClient {
    channel(topic: string, opts: { config: { private: boolean } }): HubChannel;
    removeChannel(channel: HubChannel): Promise<unknown>;
    realtime: { setAuth(token?: string | null): Promise<void> };
}

export interface HubSession {
    userId: string;
    accessToken: string;
}

export type MessageHandler = (event: string, payload: Record<string, unknown>) => void;

export interface BroadcastListener {
    topic: string;
    isPrivate: boolean;
    /** Sujet privé : le compte pour lequel l'écouteur a calculé ce sujet. Null pour un sujet public. */
    ownerId: string | null;
    /** Événements reçus ; '*' = tous. */
    events: readonly string[];
    onMessage: MessageHandler;
    /** Après chaque jonction réussie, et à l'arrivée sur un canal déjà joint. */
    onResync?: () => void;
}

export type EntryState = 'idle' | 'joining' | 'joined' | 'waiting';

export interface HubOptions {
    timers?: Timers;
    backoffMs?: (attempt: number) => number;
    log?: (message: string, detail?: unknown) => void;
}

export interface BroadcastHub {
    subscribe(listener: BroadcastListener): () => void;
    setSession(session: HubSession | null): Promise<void>;
    recheck(): void;
    snapshot(): Array<{ topic: string; state: EntryState; listeners: number; ownerId: string | null }>;
}

export const BACKOFF_BASE_MS = 1_000;
export const BACKOFF_CAP_MS = 30_000;

export function defaultBackoff(attempt: number): number {
    return Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** Math.min(Math.max(attempt, 0), 10));
}

interface Entry {
    topic: string;
    isPrivate: boolean;
    ownerId: string | null;
    listeners: Set<BroadcastListener>;
    channel: HubChannel | null;
    state: EntryState;
    attempt: number;
    timer: unknown;
    seq: number;
    retired: boolean;
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);
const noop = () => undefined;

export function createBroadcastHub(client: HubClient, options: HubOptions = {}): BroadcastHub {
    const timers = options.timers ?? realTimers;
    const backoff = options.backoffMs ?? defaultBackoff;
    const log = options.log ?? noop;
    const entries = new Map<string, Entry>();
    /**
     * Sujet → retrait de canal en cours ; toute jonction sur ce sujet l'attend.
     * Dans realtime-js (2.15.5) : quand un retrait vide `socket.channels`,
     * `removeChannel` déclenche `disconnect()` et le socket reste en état
     * « disconnecting » ~100 ms ; durant cette fenêtre, `connect()` ressort
     * tout de suite sans rien faire, donc une jonction tentée là resterait en
     * mémoire tampon sur un socket en fermeture jusqu'au timeout de jonction.
     * `system` (public) garde d'ordinaire `socket.channels` non vide, mais il
     * n'est pas permanent : lors d'une coupure réseau, son propre
     * CHANNEL_ERROR / TIMED_OUT → scheduleRejoin → release → removeChannel le
     * retire aussi. Le dernier retrait peut donc bien déclencher ce
     * `disconnect()` et sa fenêtre d'environ 100 ms ; une jonction (ex. un
     * recheck() sur `online`) qui y tombe reste en tampon jusqu'au timeout de
     * jonction (~10 s), puis le hub réessaie avec backoff et appelle onResync.
     * Effet : une jonction retardée ; onResync rattrape l'écart, rien n'est perdu.
     */
    const closing = new Map<string, Promise<void>>();
    /** Sujet → écouteurs privés dont le compte n'est pas (encore) le compte visé. */
    const parked = new Map<string, Set<BroadcastListener>>();
    let session: HubSession | null = null;
    /**
     * Session visée, mise à jour tout de suite dans setSession() avant même la
     * file `authChain` : un subscribe() synchrone qui arrive pendant un
     * changement de compte déjà demandé (mais pas encore appliqué) doit voir le
     * nouveau compte, pas l'ancien.
     */
    let targetSession: HubSession | null = null;
    /** Sérialise les changements de session ; toute jonction l'attend. */
    let authChain: Promise<void> = Promise.resolve();

    const eligible = (entry: Entry) =>
        !entry.isPrivate || (session !== null && session.userId === entry.ownerId);

    const safely = (fn: () => void, topic: string) => {
        try {
            fn();
        } catch (err) {
            log('listener threw', { topic, err });
        }
    };

    function release(entry: Entry): void {
        if (entry.timer !== null) {
            timers.clearTimeout(entry.timer);
            entry.timer = null;
        }
        entry.seq += 1;
        if (!entry.retired) entry.state = 'idle';
        const ch = entry.channel;
        entry.channel = null;
        if (!ch) return;
        let removal: Promise<void>;
        try {
            removal = Promise.resolve(client.removeChannel(ch)).then(noop, (err) =>
                log('removeChannel failed', { topic: entry.topic, err }),
            );
        } catch (err) {
            log('removeChannel threw', { topic: entry.topic, err });
            removal = Promise.resolve();
        }
        const done = Promise.all([closing.get(entry.topic) ?? Promise.resolve(), removal]).then(noop);
        closing.set(entry.topic, done);
        void done.then(() => {
            if (closing.get(entry.topic) === done) closing.delete(entry.topic);
        });
    }

    function retire(entry: Entry): void {
        entry.retired = true;
        // Ses écouteurs n'appartiennent plus (forcément) au compte visé : garés
        // plutôt que perdus, ils seront adoptés dès que leur compte le redevient.
        for (const l of entry.listeners) park(l);
        entry.listeners.clear();
        if (entries.get(entry.topic) === entry) entries.delete(entry.topic);
        release(entry);
        adoptParkedFor(entry.topic);
    }

    function park(listener: BroadcastListener): void {
        let set = parked.get(listener.topic);
        if (!set) {
            set = new Set();
            parked.set(listener.topic, set);
        }
        set.add(listener);
    }

    /** Retire un écouteur, qu'il soit garé ou raccroché à une entrée, sans jamais rien lui livrer après. */
    function detachListener(listener: BroadcastListener): void {
        const parkedSet = parked.get(listener.topic);
        if (parkedSet?.delete(listener)) {
            if (parkedSet.size === 0) parked.delete(listener.topic);
            return;
        }
        const entry = entries.get(listener.topic);
        if (entry && entry.listeners.delete(listener) && entry.listeners.size === 0 && !entry.retired) {
            retire(entry);
        }
    }

    /** Fait rentrer dans le rang les écouteurs garés d'un sujet dont le compte est (devenu) celui visé. */
    function adoptParkedFor(topic: string): void {
        const targetUser = targetSession?.userId ?? null;
        const set = parked.get(topic);
        if (!set || set.size === 0) return;
        const toAdopt = [...set].filter((l) => l.ownerId === targetUser);
        if (toAdopt.length === 0) return;
        for (const l of toAdopt) set.delete(l);
        if (set.size === 0) parked.delete(topic);
        for (const l of toAdopt) attach(l);
    }

    function adoptAllParked(): void {
        for (const topic of [...parked.keys()]) adoptParkedFor(topic);
    }

    function dispatch(entry: Entry, message: HubMessage): void {
        const event = typeof message?.event === 'string' ? message.event : '';
        if (!event) return;
        const payload = isRecord(message.payload) ? message.payload : {};
        for (const listener of [...entry.listeners]) {
            if (listener.events.includes('*') || listener.events.includes(event)) {
                safely(() => listener.onMessage(event, payload), entry.topic);
            }
        }
    }

    /**
     * Sort une entrée de l'attente sans toucher à `attempt` : la cadence de
     * reprise doit continuer d'avancer — recheck() et un jeton frais ne font
     * qu'avancer la prochaine tentative, jamais la relâcher au premier échelon.
     */
    function forceIdle(entry: Entry): void {
        if (entry.timer !== null) {
            timers.clearTimeout(entry.timer);
            entry.timer = null;
        }
        entry.state = 'idle';
    }

    function scheduleRejoin(entry: Entry): void {
        release(entry);
        entry.state = 'waiting';
        const delay = backoff(entry.attempt);
        entry.attempt += 1;
        entry.timer = timers.setTimeout(() => {
            entry.timer = null;
            if (entry.retired) return;
            entry.state = 'idle';
            void join(entry);
        }, delay);
    }

    function onStatus(entry: Entry, ch: HubChannel, status: string, err?: Error): void {
        if (entry.retired || entry.channel !== ch) return;
        if (status === 'SUBSCRIBED') {
            entry.state = 'joined';
            entry.attempt = 0;
            for (const listener of [...entry.listeners]) {
                if (listener.onResync) safely(listener.onResync, entry.topic);
            }
            return;
        }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
            log('channel lost', { topic: entry.topic, status, error: err?.message });
            scheduleRejoin(entry);
        }
    }

    async function join(entry: Entry): Promise<void> {
        if (entry.retired || entry.state !== 'idle' || !eligible(entry)) return;
        entry.state = 'joining';
        const seq = ++entry.seq;
        const superseded = () => entry.retired || entry.seq !== seq;
        await authChain;
        await (closing.get(entry.topic) ?? Promise.resolve());
        if (superseded()) return;
        if (!eligible(entry)) {
            entry.state = 'idle';
            return;
        }
        if (entry.isPrivate && session) {
            try {
                await client.realtime.setAuth(session.accessToken);
            } catch (err) {
                log('setAuth failed before join', { topic: entry.topic, err });
            }
            if (superseded()) return;
            if (!eligible(entry)) {
                entry.state = 'idle';
                return;
            }
        }
        const lateClose = closing.get(entry.topic);
        if (lateClose) {
            await lateClose;
            if (superseded()) return;
        }
        try {
            const ch = client.channel(entry.topic, { config: { private: entry.isPrivate } });
            entry.channel = ch;
            ch.on('broadcast', { event: '*' }, (message) => {
                if (entry.channel === ch && !entry.retired) dispatch(entry, message);
            });
            ch.subscribe((status, err) => onStatus(entry, ch, String(status), err));
        } catch (err) {
            // channel()/on()/subscribe() qui lève (ex. pas de WebSocket) ne doit
            // jamais laisser l'entrée bloquée en 'joining' : on retente comme un
            // canal perdu, sans jamais abandonner.
            log('join failed', { topic: entry.topic, err });
            scheduleRejoin(entry);
        }
    }

    /**
     * Raccroche un écouteur dont le compte (ou l'absence de compte, en public)
     * est déjà celui visé à l'entrée de son sujet, en la créant si besoin.
     * N'est jamais appelé pour un écouteur dont le compte n'est pas (encore)
     * celui visé — c'est tout l'objet de `park`/`subscribe`.
     */
    function attach(listener: BroadcastListener): void {
        const ownerId = listener.isPrivate ? listener.ownerId : null;
        let entry = entries.get(listener.topic);
        if (entry && (entry.isPrivate !== listener.isPrivate || entry.ownerId !== ownerId)) {
            // L'entrée en place appartient à un autre compte (ou visibilité) que
            // celui visé — jamais au compte visé lui-même, puisque `ownerId` l'est
            // déjà ici : elle est donc toujours sans risque à évincer.
            retire(entry);
            // `retire` peut avoir fait adopter un écouteur garé du même sujet
            // (même compte visé) et recréé l'entrée : on la reprend si c'est le cas.
            entry = entries.get(listener.topic);
        }
        if (!entry) {
            entry = {
                topic: listener.topic,
                isPrivate: listener.isPrivate,
                ownerId,
                listeners: new Set(),
                channel: null,
                state: 'idle',
                attempt: 0,
                timer: null,
                seq: 0,
                retired: false,
            };
            entries.set(listener.topic, entry);
            // D'autres écouteurs du même sujet et du même compte visé pouvaient
            // déjà être garés (plusieurs montages avant la connexion, par ex.).
            adoptParkedFor(listener.topic);
        }
        entry.listeners.add(listener);
        if (entry.state === 'joined') {
            if (listener.onResync) safely(listener.onResync, entry.topic);
        } else if (entry.state === 'idle') {
            void join(entry);
        }
    }

    function subscribe(listener: BroadcastListener): () => void {
        if (listener.isPrivate && !listener.ownerId) {
            throw new Error(`broadcastHub: private topic ${listener.topic} needs an ownerId`);
        }
        const ownerId = listener.isPrivate ? listener.ownerId : null;
        const targetUser = targetSession?.userId ?? null;
        if (listener.isPrivate && ownerId !== targetUser) {
            // Pas (encore) le compte visé : garé, jamais de message, adopté dès
            // que ce compte le devient (setSession, ou une entrée qui se libère).
            park(listener);
        } else {
            attach(listener);
        }
        return () => detachListener(listener);
    }

    async function setSession(next: HubSession | null): Promise<void> {
        targetSession = next;
        // Tout de suite, avant même la file `authChain` : un écouteur garé pour
        // ce compte n'a pas à attendre que la session soit réellement appliquée.
        adoptAllParked();
        const run = async () => {
            const prev = session;
            if ((prev?.userId ?? null) === (next?.userId ?? null)) {
                session = next;
                if (next && prev && next.accessToken !== prev.accessToken) {
                    try {
                        await client.realtime.setAuth(next.accessToken);
                    } catch (err) {
                        log('setAuth failed on token refresh', err);
                    }
                }
                // Même compte : un sujet privé qui attendait sa prochaine tentative
                // (ex. onglet caché des heures, jeton expiré) la relance tout de
                // suite avec le jeton courant au lieu d'attendre sa cadence de
                // reprise — la boucle après `await step` (plus bas) la rejoint.
                if (next) {
                    for (const entry of entries.values()) {
                        if (entry.isPrivate && entry.ownerId === next.userId && entry.state === 'waiting') {
                            forceIdle(entry);
                        }
                    }
                }
                return;
            }
            session = next;
            for (const entry of [...entries.values()]) {
                if (!entry.isPrivate) continue;
                if (next && entry.ownerId === next.userId) release(entry);
                else retire(entry);
            }
            await Promise.all([...closing.values()]);
            try {
                await client.realtime.setAuth(next?.accessToken ?? null);
            } catch (err) {
                log('setAuth failed on session change', err);
            }
        };
        const step = authChain.then(run);
        authChain = step.then(noop, noop);
        await step;
        for (const entry of [...entries.values()]) {
            if (entry.state === 'idle') void join(entry);
        }
    }

    function recheck(): void {
        for (const entry of [...entries.values()]) {
            // Force une tentative tout de suite (onglet redevenu visible, réseau
            // revenu) sans remettre `attempt` à zéro : un sujet toujours refusé ne
            // doit pas dépasser une tentative par 30 s en plus de celle-ci.
            if (entry.state === 'waiting') forceIdle(entry);
            if (entry.state === 'idle') void join(entry);
        }
    }

    function snapshot() {
        return [...entries.values()].map((e) => ({
            topic: e.topic,
            state: e.state,
            listeners: e.listeners.size,
            ownerId: e.ownerId,
        }));
    }

    return { subscribe, setSession, recheck, snapshot };
}

export interface AuthLike {
    onAuthStateChange(
        callback: (event: string, session: { access_token: string; user: { id: string } } | null) => void,
    ): { data: { subscription: { unsubscribe(): void } } };
}

/** Relaie chaque session au hub. Hors du rappel d'auth : setAuth(null) relit la session et y bloquerait. */
export function bindHubToAuth(hub: BroadcastHub, auth: AuthLike, timers: Timers = realTimers): () => void {
    const pending = new Set<unknown>();
    const { data } = auth.onAuthStateChange((_event, s) => {
        const next = s?.access_token && s.user?.id ? { userId: s.user.id, accessToken: s.access_token } : null;
        const handle = timers.setTimeout(() => {
            pending.delete(handle);
            void hub.setSession(next);
        }, 0);
        pending.add(handle);
    });
    return () => {
        data.subscription.unsubscribe();
        // Un relais encore en attente ne doit pas appeler setSession après off() :
        // on annule tout minuteur restant.
        for (const handle of pending) timers.clearTimeout(handle);
        pending.clear();
    };
}

export interface LifecycleTarget {
    addEventListener(type: string, fn: () => void): void;
    removeEventListener(type: string, fn: () => void): void;
}

/** Réseau revenu ou onglet de nouveau visible : rejoindre tout de suite ce qui attend. */
export function bindHubToLifecycle(
    hub: BroadcastHub,
    win: LifecycleTarget,
    doc: LifecycleTarget & { visibilityState: string },
): () => void {
    const onOnline = () => hub.recheck();
    const onVisibility = () => {
        if (doc.visibilityState === 'visible') hub.recheck();
    };
    win.addEventListener('online', onOnline);
    doc.addEventListener('visibilitychange', onVisibility);
    return () => {
        win.removeEventListener('online', onOnline);
        doc.removeEventListener('visibilitychange', onVisibility);
    };
}
